import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { usePublicConfig } from '@/features/settings/hooks/useServicesSettings';

import { useConsent } from '../hooks/useConsent';
import { useConsentScript } from '../hooks/useConsentScript';
import { CookieCategory } from '../types';
import type { FbqFunction } from './metaPixelEvents';

/**
 * Meta Pixel (fbq), vincolato al consenso di PROFILAZIONE.
 *
 * Come GA4/Google Ads: nessuno snippet in index.html e nessun fallback
 * <noscript> (sparerebbero prima/senza consenso) — script iniettato/rimosso a
 * runtime. L'ID arriva da GET /settings/public-config (configurabile da
 * admin, /dashboard/admin/tracking); null = feature spenta.
 *
 * Semantica canonica dello snippet Meta: lo stub fbq con la queue va definito
 * PRIMA che fbevents.js carichi (lo script consuma la queue al load). Qui lo
 * stub + init + PageView stanno in un effect registrato prima di
 * useConsentScript, quindi eseguito prima dell'iniezione.
 */
function ensureFbqStub(): FbqFunction {
  if (window.fbq) return window.fbq;
  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue.push(args);
    }
  } as FbqFunction;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];
  window.fbq = fbq;
  if (!window._fbq) window._fbq = fbq;
  return fbq;
}

function clearMetaCookies() {
  try {
    const domain = window.location.hostname;
    document.cookie.split(';').forEach((c) => {
      const name = (c.split('=')[0] ?? '').trim();
      if (name === '_fbp' || name === '_fbc') {
        const expire = 'expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
        document.cookie = `${name}=;${expire}`;
        document.cookie = `${name}=;${expire};domain=.${domain}`;
      }
    });
  } catch {
    /* best-effort */
  }
}

function MetaPixelLoader({ pixelId, pageViewEnabled }: { pixelId: string; pageViewEnabled: boolean }) {
  const granted = useConsent(CookieCategory.PROFILING);
  const location = useLocation();
  const prevPathRef = useRef<string | null>(null);

  // Stub + init (sempre) + PageView iniziale (se abilitato), PRIMA dell'iniezione.
  useEffect(() => {
    if (!granted) return;
    const fbq = ensureFbqStub();
    fbq('init', pixelId);
    if (pageViewEnabled) {
      fbq('track', 'PageView');
    }
  }, [granted, pixelId, pageViewEnabled]);

  useConsentScript(CookieCategory.PROFILING, {
    id: 'meta-pixel',
    src: 'https://connect.facebook.net/en_US/fbevents.js',
    onRevoke: () => {
      window.fbq = undefined;
      window._fbq = undefined;
      clearMetaCookies();
    },
  });

  // PageView a ogni cambio route (SPA); il primo è già inviato dall'init.
  useEffect(() => {
    const path = location.pathname + location.search;
    if (!granted || !pageViewEnabled || typeof window.fbq !== 'function') {
      prevPathRef.current = path;
      return;
    }
    if (prevPathRef.current !== null && prevPathRef.current !== path) {
      window.fbq('track', 'PageView');
    }
    prevPathRef.current = path;
  }, [granted, pageViewEnabled, location.pathname, location.search]);

  return null;
}

export function MetaPixel() {
  const { data } = usePublicConfig();
  const pixelId = data?.metaPixelId;

  if (!pixelId) return null;
  // events null/assente ⇒ default abilitato (!== false).
  const pageViewEnabled = data?.metaPixelEvents?.pageView !== false;
  return <MetaPixelLoader pixelId={pixelId} pageViewEnabled={pageViewEnabled} />;
}
